import axios from "axios";
import normalize from "Utils/normalizer";
import errorHandler from "Utils/error-handler";
import useSession from "Hooks/session.hook";
import { getWithExpiry } from "Helpers/getset-with-expiry";
import { getAccessToken } from "Helpers/session";
import { toLower, omitBy, isEmpty, isNil, map, some, isArray } from "lodash";
import { DefaultFilter, Pagination, Sorting, Statuses } from "./../types/common.types";
import {
  CustomerFormInputType,
  CustomerApprovalStatus,
  CustomerCompanyFormInputType,
  OrgType,
} from "./../types/customer-form.types";

const url = `${process.env.API_URL}/${process.env.API_VER}`;

interface Filters extends DefaultFilter {
  orFields?: string;
  orgType?: string;
  customerType?: Array<string>;
}

export interface CustomerApprovalInput {
  approvalStatus: "APPROVED" | "REJECTED";
  orgID: string;
  rejectedReason?: string;
}

export const fetchCustomer = async (orgID: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer/fetch`,
      data: { orgID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return normalize.response.customer_details(response.data.org);
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchCustomer(orgID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// double confirm with BE
export const fetchBearerToken = async (orgID: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer/bearer_token/fetch`,
      data: { orgID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.bearerToken
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      // fetch requester failed
      if (error.code === 88510)
        return await createBearerToken(orgID)

      const callback = async () => await fetchBearerToken(orgID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const createBearerToken = async (orgID: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer/bearer_token/create`,
      data: { orgID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.bearerToken
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await createBearerToken(orgID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchCustomerDeleted = async (orgID: string) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer/deleted/fetch`,
      data: { orgID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return !isEmpty(response.data.users)
      ? map(response.data.users, (user: any) => {
          return normalize.response.account(user);
        })
      : [];
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchCustomerDeleted(orgID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchCustomerList = async (
  filters: Filters,
  orgID: string[] | string,
  approvalStatus?: CustomerApprovalStatus,
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          orgID: isArray(orgID) ? orgID : [orgID],
          approvalStatus,
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.org;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await fetchCustomerList(filters, orgID, approvalStatus, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchCustomerListCount = async (
  filters: Filters,
  orgID: string[],
  approvalStatus: CustomerApprovalStatus,
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer/pending/count/fetch`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          orgID,
          approvalStatus,
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await fetchCustomerListCount(filters, orgID, approvalStatus, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchDeletedCustomerList = async (
  filters: Filters,
  orgID: string[],
  approvalStatus?: CustomerApprovalStatus,
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer/deleted/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          orgID,
          approvalStatus,
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.org;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await fetchDeletedCustomerList(filters, orgID, approvalStatus, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const createCustomer = async (
  data: CustomerFormInputType | CustomerCompanyFormInputType,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer/register/create`,
      data:
        data.customerType === "INDIVIDUAL"
          ? normalize.model.individual_customer(data)
          : normalize.model.company_customer(data as CustomerCompanyFormInputType),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createCustomer(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateCustomer = async (
  data: CustomerFormInputType | CustomerCompanyFormInputType,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer/update`,
      data:
        data.customerType === "INDIVIDUAL"
          ? normalize.model.individual_customer(data)
          : normalize.model.company_customer(data as CustomerCompanyFormInputType),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createCustomer(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const customerApproval = async (data: CustomerApprovalInput) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer/approval/update`,
      data: data,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await customerApproval(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const deleteCustomer = async (orgID: string, tempDeletedReason: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer/delete`,
      data: { orgID, tempDeletedReason },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await deleteCustomer(orgID, tempDeletedReason);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const restoreCustomer = async (orgID: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer/restore/update`,
      data: { orgID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await restoreCustomer(orgID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};
